import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import {
    Eye, EyeOff, Loader2, Sparkles, CheckCircle2, XCircle,
    ShieldCheck, Info,
} from 'lucide-react';
import api from '../../lib/api';

/**
 * Homly — Onboarding Solo Agent
 * ─────────────────────────────────
 * Inscription rapide (30s) pour agent commercial indépendant.
 *
 * Sécurité :
 *   • Numéro RSAC obligatoire, validé par algorithme Luhn INSEE
 *   • Vérif en temps réel (endpoint /api/auth/rsac/check)
 *   • Case légale obligatoire (déclaration sur l'honneur)
 *
 * Flow :
 *   Form → POST /register/solo → JWT reçu → login auto → /agent
 */
export default function RegisterSoloPage() {
    const { loginWithToken } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [rsacStatus, setRsacStatus] = useState({ valid: null, available: null, formatted: null, reason: null });
    const [form, setForm] = useState({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        password: '',
        rsac_number: '',
        legal_accepted: false,
        marketing_optin: false,
    });

    // ── Vérification RSAC en temps réel (debounced) ──────────
    useEffect(() => {
        const digits = form.rsac_number.replace(/\D/g, '');
        if (digits.length !== 9) {
            setRsacStatus({ valid: null, available: null, formatted: null, reason: null });
            return;
        }
        const timer = setTimeout(async () => {
            try {
                const res = await api.get('/auth/rsac/check', { params: { rsac: digits } });
                setRsacStatus(res.data);
            } catch {
                setRsacStatus({ valid: null, available: null, formatted: null, reason: null });
            }
        }, 400);
        return () => clearTimeout(timer);
    }, [form.rsac_number]);

    // ── Formatage automatique du RSAC (3 groupes de 3) ─────────
    const handleRsacChange = (v) => {
        const digits = v.replace(/\D/g, '').slice(0, 9);
        const groups = [digits.slice(0, 3), digits.slice(3, 6), digits.slice(6, 9)].filter(Boolean);
        setForm(f => ({ ...f, rsac_number: groups.join(' ') }));
    };

    const passwordChecks = useMemo(() => ({
        length: form.password.length >= 10,
        upper: /[A-Z]/.test(form.password),
        digit: /\d/.test(form.password),
    }), [form.password]);

    const canSubmit =
        form.first_name.trim() &&
        form.last_name.trim() &&
        form.email.trim() &&
        form.phone.trim() &&
        passwordChecks.length && passwordChecks.upper && passwordChecks.digit &&
        rsacStatus.valid === true &&
        rsacStatus.available === true &&
        form.legal_accepted;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setLoading(true);
        try {
            const digits = form.rsac_number.replace(/\D/g, '');
            const res = await api.post('/auth/register/solo', {
                first_name: form.first_name.trim(),
                last_name: form.last_name.trim(),
                email: form.email.trim().toLowerCase(),
                phone: form.phone.trim(),
                password: form.password,
                rsac_number: digits,
                legal_accepted: true,
                marketing_optin: form.marketing_optin,
            });
            // Stocker le token et rediriger
            const token = res.data.access_token;
            await loginWithToken(token);
            toast.success('Bienvenue chez Homly ! 🎉');
            setTimeout(() => navigate('/agent'), 400);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const msg = typeof detail === 'string'
                ? detail
                : (detail?.message || 'Inscription impossible. Réessayez.');
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    // ── RSAC status color / message ───────────────────────────
    const rsacFeedback = () => {
        const digits = form.rsac_number.replace(/\D/g, '');
        if (digits.length === 0) return null;
        if (digits.length < 9) return { icon: Info, tone: 'muted', text: `${digits.length}/9 chiffres` };
        if (rsacStatus.valid === false) return { icon: XCircle, tone: 'error', text: rsacStatus.reason || 'Numéro RSAC invalide' };
        if (rsacStatus.valid === true && rsacStatus.available === false)
            return { icon: XCircle, tone: 'error', text: 'Ce numéro RSAC est déjà rattaché à un compte' };
        if (rsacStatus.valid === true && rsacStatus.available === true)
            return { icon: CheckCircle2, tone: 'ok', text: `Numéro valide (${rsacStatus.formatted})` };
        return null;
    };
    const rsacInfo = rsacFeedback();

    return (
        <div className="min-h-screen bg-[#050308] text-white flex items-center justify-center p-4 sm:p-8" data-testid="register-solo-page">
            {/* Halos DA Homly */}
            <div className="pointer-events-none fixed inset-0 overflow-hidden">
                <div className="absolute -top-40 -left-40 w-[500px] h-[500px] bg-[#681793]/25 blur-[120px] rounded-full" />
                <div className="absolute -bottom-40 -right-40 w-[600px] h-[600px] bg-[#FF914D]/15 blur-[140px] rounded-full" />
            </div>

            <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35 }}
                className="relative w-full max-w-lg bg-black/60 backdrop-blur-xl border border-[#A855F7]/20 rounded-3xl p-6 sm:p-8"
            >
                {/* Header */}
                <div className="flex items-center gap-2 text-xs font-bold tracking-[3px] text-[#FF914D] mb-3">
                    <Sparkles className="w-4 h-4" />
                    MODE SOLO
                </div>
                <h1 className="text-3xl sm:text-4xl font-extrabold leading-tight bg-gradient-to-r from-[#A855F7] to-[#FF914D] bg-clip-text text-transparent mb-2">
                    Créez votre compte agent
                </h1>
                <p className="text-sm text-[#C7BEE6] mb-6">
                    Réservé aux agents commerciaux indépendants inscrits au Registre Spécial (RSAC).
                    Prêt en 30 secondes.
                </p>

                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Prénom + Nom */}
                    <div className="grid grid-cols-2 gap-3">
                        <FieldWrapper label="Prénom">
                            <Input
                                type="text" required
                                value={form.first_name}
                                onChange={(e) => setForm(f => ({ ...f, first_name: e.target.value }))}
                                className={inputCls}
                                data-testid="solo-first-name"
                            />
                        </FieldWrapper>
                        <FieldWrapper label="Nom">
                            <Input
                                type="text" required
                                value={form.last_name}
                                onChange={(e) => setForm(f => ({ ...f, last_name: e.target.value }))}
                                className={inputCls}
                                data-testid="solo-last-name"
                            />
                        </FieldWrapper>
                    </div>

                    {/* Email */}
                    <FieldWrapper label="Email professionnel">
                        <Input
                            type="email" required autoComplete="email"
                            placeholder="vous@example.com"
                            value={form.email}
                            onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                            className={inputCls}
                            data-testid="solo-email"
                        />
                    </FieldWrapper>

                    {/* Téléphone */}
                    <FieldWrapper label="Téléphone">
                        <Input
                            type="tel" required autoComplete="tel"
                            placeholder="06 12 34 56 78"
                            value={form.phone}
                            onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))}
                            className={inputCls}
                            data-testid="solo-phone"
                        />
                    </FieldWrapper>

                    {/* Mot de passe */}
                    <FieldWrapper label="Mot de passe">
                        <div className="relative">
                            <Input
                                type={showPassword ? 'text' : 'password'} required
                                autoComplete="new-password"
                                placeholder="10 caractères min., 1 majuscule, 1 chiffre"
                                value={form.password}
                                onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))}
                                className={`${inputCls} pr-10`}
                                data-testid="solo-password"
                            />
                            <button type="button" onClick={() => setShowPassword(v => !v)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8A7DB8] hover:text-white">
                                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                        </div>
                        {form.password && (
                            <div className="mt-2 flex flex-wrap gap-2 text-[11px]" data-testid="solo-password-checks">
                                <Chk ok={passwordChecks.length}>10 caractères</Chk>
                                <Chk ok={passwordChecks.upper}>1 majuscule</Chk>
                                <Chk ok={passwordChecks.digit}>1 chiffre</Chk>
                            </div>
                        )}
                    </FieldWrapper>

                    {/* RSAC */}
                    <FieldWrapper label="Numéro RSAC (Registre spécial des Agents commerciaux)">
                        <Input
                            type="text" required inputMode="numeric"
                            placeholder="812 345 678"
                            value={form.rsac_number}
                            onChange={(e) => handleRsacChange(e.target.value)}
                            className={inputCls}
                            data-testid="solo-rsac"
                        />
                        {rsacInfo && (
                            <div
                                className={`mt-2 flex items-center gap-1.5 text-[11px] ${
                                    rsacInfo.tone === 'ok' ? 'text-[#8FE39B]' :
                                    rsacInfo.tone === 'error' ? 'text-[#FF9DA5]' : 'text-[#8A7DB8]'
                                }`}
                                data-testid="solo-rsac-feedback"
                            >
                                <rsacInfo.icon className="w-3.5 h-3.5" />
                                {rsacInfo.text}
                            </div>
                        )}
                        <p className="mt-1.5 text-[11px] text-[#8A7DB8] flex items-start gap-1.5">
                            <Info className="w-3 h-3 mt-0.5 shrink-0" />
                            Ce numéro figure sur votre récépissé RSAC délivré par le Greffe du Tribunal de Commerce.
                        </p>
                    </FieldWrapper>

                    {/* Case légale obligatoire */}
                    <label className="flex items-start gap-2.5 text-xs text-[#C7BEE6] cursor-pointer p-3 rounded-xl border border-[#A855F7]/15 bg-black/30 hover:border-[#A855F7]/30 transition-colors" data-testid="solo-legal-block">
                        <input
                            type="checkbox" required
                            checked={form.legal_accepted}
                            onChange={(e) => setForm(f => ({ ...f, legal_accepted: e.target.checked }))}
                            className="mt-0.5 accent-[#A855F7] w-4 h-4"
                            data-testid="solo-legal-checkbox"
                        />
                        <span className="leading-snug">
                            <ShieldCheck className="w-3.5 h-3.5 inline text-[#FF914D] mr-1" />
                            Je déclare sur l&apos;honneur être <b>entrepreneur individuel, agissant en ma
                            qualité d&apos;agent commercial, régulièrement inscrit au Registre spécial des
                            Agents commerciaux</b> sous le numéro <span className="text-white font-mono">
                            {rsacStatus.formatted || (form.rsac_number || 'xxx xxx xxx')}
                            </span>.
                        </span>
                    </label>

                    {/* Marketing opt-in (facultatif) */}
                    <label className="flex items-center gap-2.5 text-xs text-[#8A7DB8] cursor-pointer" data-testid="solo-marketing-block">
                        <input
                            type="checkbox"
                            checked={form.marketing_optin}
                            onChange={(e) => setForm(f => ({ ...f, marketing_optin: e.target.checked }))}
                            className="accent-[#A855F7] w-4 h-4"
                            data-testid="solo-marketing-checkbox"
                        />
                        <span>Recevoir les nouveautés et conseils de collecte d&apos;avis</span>
                    </label>

                    {/* Submit */}
                    <Button
                        type="submit"
                        disabled={!canSubmit || loading}
                        data-testid="solo-submit"
                        className="w-full bg-gradient-to-r from-[#681793] to-[#FF914D] text-white font-bold py-6 rounded-full text-base shadow-lg shadow-[#681793]/30 hover:opacity-95 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        {loading ? (
                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Création en cours...</>
                        ) : (
                            'Créer mon compte solo'
                        )}
                    </Button>
                </form>

                {/* Footer */}
                <div className="mt-6 pt-6 border-t border-white/5 text-center text-xs text-[#8A7DB8]">
                    Déjà un compte ?{' '}
                    <Link to="/login" className="text-[#FF914D] font-semibold hover:underline" data-testid="solo-back-to-login">
                        Se connecter
                    </Link>
                </div>
            </motion.div>
        </div>
    );
}

// ── Sous-composants ───────────────────────────────────────────
const inputCls = 'bg-black/40 border-white/10 focus:border-[#A855F7]/50 text-white placeholder:text-[#5A4C7A] rounded-xl';

const FieldWrapper = ({ label, children }) => (
    <div>
        <Label className="text-[#C7BEE6] text-xs font-semibold tracking-wide mb-1.5 block">{label}</Label>
        {children}
    </div>
);

const Chk = ({ ok, children }) => (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${
        ok ? 'bg-emerald-500/10 text-emerald-300' : 'bg-white/5 text-[#8A7DB8]'
    }`}>
        {ok ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
        {children}
    </span>
);
